import type { GameMountContext, GameRealtimeContext } from "./types";

export interface RunnerSearchParamsOptions {
  gameId: string;
  locale?: string;
  realtime?: GameRealtimeContext;
  /**
   * When true, the runner will emit resize events so the host iframe can sync its height to the game content.
   * Defaults to false to avoid extra messaging for fixed-aspect games.
   */
  autoHeight?: boolean;
  extraParams?: Record<string, string | number | boolean | undefined>;
}

export interface RunnerUrlOptions extends RunnerSearchParamsOptions {
  path?: string;
}

export const DEFAULT_RUNNER_PATH = "/game-runner.html";

export function encodeRunnerSearchParams(options: RunnerSearchParamsOptions): URLSearchParams {
  const params = new URLSearchParams();
  params.set("game", options.gameId);

  if (options.locale) {
    params.set("locale", options.locale);
  }

  const peerServer = options.realtime?.peerServer?.trim();
  if (peerServer) {
    params.set("peerServer", peerServer);
  }

  const iceServersEndpoint = options.realtime?.iceServersEndpoint?.trim();
  if (iceServersEndpoint) {
    params.set("iceServersEndpoint", iceServersEndpoint);
  }

  if (options.extraParams) {
    for (const [key, value] of Object.entries(options.extraParams)) {
      if (value === undefined || value === null) {
        continue;
      }

      if (typeof value === "boolean") {
        params.set(key, value ? "1" : "0");
      } else {
        params.set(key, String(value));
      }
    }
  }

  if (options.autoHeight) {
    params.set("autoHeight", "1");
  }

  return params;
}

export function buildRunnerSrc(options: RunnerUrlOptions): string {
  const path = options.path ?? DEFAULT_RUNNER_PATH;
  const params = encodeRunnerSearchParams(options);
  return `${path}?${params.toString()}`;
}

export function parseBooleanParam(value: string | null | undefined): boolean | undefined {
  if (value === null || value === undefined) {
    return undefined;
  }

  const normalized = value.toLowerCase();
  if (normalized === "1" || normalized === "true") {
    return true;
  }

  if (normalized === "0" || normalized === "false") {
    return false;
  }

  return undefined;
}

export interface ParsedRunnerSearch {
  gameId: string;
  context: GameMountContext;
  autoHeight?: boolean;
}

export function parseRunnerSearchParams(params: URLSearchParams): ParsedRunnerSearch {
  const gameId = params.get("game") ?? "";
  const localeParam = params.get("locale");
  const locale = localeParam && localeParam.trim().length > 0 ? localeParam : undefined;
  const peerServerParam = params.get("peerServer");
  const peerServer = peerServerParam && peerServerParam.trim().length > 0 ? peerServerParam : undefined;
  const iceServersEndpointParam = params.get("iceServersEndpoint");
  const iceServersEndpoint =
    iceServersEndpointParam && iceServersEndpointParam.trim().length > 0
      ? iceServersEndpointParam
      : undefined;
  const autoHeight = parseBooleanParam(params.get("autoHeight"));

  const context: GameMountContext = {};
  if (locale) {
    context.locale = locale;
  }
  if (peerServer || iceServersEndpoint) {
    context.realtime = { peerServer, iceServersEndpoint };
  }

  return { gameId, context, autoHeight };
}
